import { ACTION_NAMES, type ActionName, type ArgsOf, type ResultOf } from '@hbac/contract';
import type { Transport } from './transport';

/**
 * A typed method per action, generated from the contract.
 *
 * Calling an action that does not exist, or passing the wrong number or type of
 * arguments, is now a compile error rather than a runtime "Hành động không hợp
 * lệ" from the server.
 */
export type Api = {
  [A in ActionName]: (...args: ArgsOf<A>) => Promise<ResultOf<A>>;
};

export function createApi(transport: Transport): Api {
  const api = {} as Api;
  for (const action of ACTION_NAMES) {
    // The cast is confined to this loop: ACTION_NAMES is derived from the same
    // const object the Api type is, so every key is a valid ActionName.
    (api as Record<string, unknown>)[action] = (...args: unknown[]) =>
      transport.call(action, args as ArgsOf<typeof action>) as Promise<ResultOf<typeof action>>;
  }
  return api;
}

/**
 * The live instance, wired in main.tsx. Screens import this and never a
 * transport, so swapping the backend touches one file.
 */
let current: Api | null = null;

export function installApi(transport: Transport): Api {
  current = createApi(transport);
  return current;
}

export function getApi(): Api {
  if (!current) throw new Error('API not installed: call installApi() in main.tsx first.');
  return current;
}
